// Enrollment Status Service - typed API client for Custom Disclaimer enrollment status
// Backend endpoint: GET /custom-disclaimers/enrollment-status

import { apiClient } from './apiClient';
import { ServiceError } from './ServiceError';

export interface EnrollmentStatus {
  organizationEnrolledUserCount: number;
  disclaimerCounts: DisclaimerEnrollmentCount[];
}

export interface DisclaimerEnrollmentCount {
  disclaimerId: string;
  usersReceiving: number;
}

const isNonNegativeSafeInteger = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;

const isDisclaimerEnrollmentCount = (value: unknown): value is DisclaimerEnrollmentCount => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Record<string, unknown>;

  return (
    typeof candidate.disclaimerId === 'string' &&
    candidate.disclaimerId.length > 0 &&
    isNonNegativeSafeInteger(candidate.usersReceiving)
  );
};

// A malformed individual record, a missing organization count, or a duplicate disclaimer id is
// just as unusable to the UI as a malformed envelope: enrollment-status rendering assumes every
// disclaimer is represented exactly once with valid counts, so validate the full shape rather
// than only the envelope.
const isEnrollmentStatus = (value: unknown): value is EnrollmentStatus => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Record<string, unknown>;

  if (!isNonNegativeSafeInteger(candidate.organizationEnrolledUserCount)) {
    return false;
  }

  if (!Array.isArray(candidate.disclaimerCounts) || !candidate.disclaimerCounts.every(isDisclaimerEnrollmentCount)) {
    return false;
  }

  const disclaimerIds = candidate.disclaimerCounts.map(
    (count) => (count as DisclaimerEnrollmentCount).disclaimerId,
  );

  return new Set(disclaimerIds).size === disclaimerIds.length;
};

class EnrollmentStatusService {
  // Get the organization-wide enrollment status for the Custom Disclaimers feature
  async getEnrollmentStatus(): Promise<EnrollmentStatus> {
    const response = await apiClient.get<EnrollmentStatus>('/custom-disclaimers/enrollment-status');

    if (!isEnrollmentStatus(response.data)) {
      throw new ServiceError('Unable to load enrollment status. Please try again.', {
        code: 'ENROLLMENT_STATUS_MALFORMED',
      });
    }

    return response.data;
  }
}

export const enrollmentStatusService = new EnrollmentStatusService();
